import { useState } from 'react';
import {
  Plus,
  Search,
  X,
  UserPlus,
  Eye,
  ChevronDown,
  Check,
  Phone,
  Mail,
  Users,
  Briefcase,
  BadgeCheck,
  UserCog,
  ShieldAlert,
  ArrowUpRight,
  Clock,
  Lock,
  Unlock,
  User,
} from 'lucide-react';

/* ------------------------------------------------------------------ */
/*  Static data (mock — swap for your people API)                      */
/*  NOTE: TRAINING fields/rows are intentionally commented out per     */
/*  the current scope — they're kept, not deleted.                    */
/* ------------------------------------------------------------------ */
const PEOPLE = [
  {
    id: 1,
    name: 'Jill Quartey',
    email: 'jill.quartey@apexsolutions.com.au',
    type: 'Agency Compliance Officer',
    typeKind: 'compliance',
    status: 'Active',
    statusKind: 'active',
    // TRAINING — COMMENTED OUT: awaiting: 'Training · 3 days',
    start: 'Company · 12 Jul 2026',
    last: '2h ago',
    initial: 'JQ',
  },
  {
    id: 2,
    name: 'Aaron Budellseer',
    email: 'aaron.b@apexsolutions.com.au',
    type: 'Corporate Owner',
    typeKind: 'owner',
    status: 'Active',
    statusKind: 'active',
    // TRAINING — COMMENTED OUT: awaiting: 'Training · 2 days',
    start: 'Company · 12 Jul 2026',
    last: '3h ago',
    initial: 'AB',
  },
  {
    id: 3,
    name: 'Ronan Aris',
    email: 'ronan.aris@apexsolutions.com.au',
    type: 'Trainer / Manager',
    typeKind: 'trainer',
    status: 'Onboarding',
    statusKind: 'onboarding',
    awaiting: 'Credited trial · 1 day',
    start: 'Company · 5 Jul 2026',
    last: '5h ago',
    initial: 'RA',
  },
  {
    id: 4,
    name: 'Michael Thompson',
    email: 'm.thompson@apexsolutions.com.au',
    type: 'Agency Officer',
    typeKind: 'officer',
    status: 'Active',
    statusKind: 'active',
    awaiting: 'Document · 4 days',
    start: 'Company · 25 Jun 2026',
    last: '2 years ago',
    initial: 'MT',
  },
  {
    id: 5,
    name: 'Lucy Ashworth',
    email: 'lucy.ashworth@apexsolutions.com.au',
    type: 'Trained / Manager',
    typeKind: 'trained',
    status: 'Active',
    statusKind: 'active',
    awaiting: 'Onboarding · 5 days',
    start: 'Company · 8 Jun 2026',
    last: '5h ago',
    initial: 'LA',
  },
  {
    id: 6,
    name: 'Jon Vale',
    email: 'jon.vale@apexsolutions.com.au',
    type: 'Trained / Manager',
    typeKind: 'trained',
    status: 'Suspended',
    statusKind: 'suspended',
    awaiting: 'Credited trial · 1 day',
    start: 'Company · 24 Jan 2026',
    last: '5 years ago',
    initial: 'JV',
  },
  {
    id: 8,
    name: 'Ella Morgan',
    email: 'ella.morgan@apexsolutions.com.au',
    type: 'Agency Officer',
    typeKind: 'officer',
    status: 'Pending',
    statusKind: 'pending',
    awaiting: 'Awaiting approval · 2 days',
    start: 'Company · 18 Jul 2026',
    last: '4h ago',
    initial: 'EM',
  },
  {
    id: 9,
    name: 'Daniel Fong',
    email: 'daniel.fong@apexsolutions.com.au',
    type: 'Corporate Owner',
    typeKind: 'owner',
    status: 'In review',
    statusKind: 'review',
    awaiting: 'Compliance review · 1 day',
    start: 'Company · 20 Jul 2026',
    last: '1h ago',
    initial: 'DF',
  },
  {
    id: 7,
    name: 'Paul Robertson',
    email: 'paul.robertson@apexsolutions.com.au',
    type: 'Independent Consultant',
    typeKind: 'consultant',
    status: 'Blocked',
    statusKind: 'blocked',
    awaiting: 'Document · 3 days',
    start: 'Company · 12 Jul 2026',
    last: '2 years ago',
    initial: 'PR',
  },
];

/* Soft, muted avatar tints — pastel background + readable matching text */
const AVATARS = {
  compliance: { bg: 'rgba(55,90,231,0.10)', text: '#4C5FCC' },
  owner: { bg: 'rgba(100,116,139,0.12)', text: '#55657C' },
  trainer: { bg: 'rgba(245,158,11,0.12)', text: '#B8860B' },
  officer: { bg: 'rgba(100,116,139,0.12)', text: '#55657C' },
  trained: { bg: 'rgba(139,92,246,0.12)', text: '#7C5CC4' },
  consultant: { bg: 'rgba(16,185,129,0.12)', text: '#0E9E6E' },
};

const TYPE_BADGE = {
  compliance: 'bg-brand/10 text-brand ring-brand/20',
  owner: 'bg-slate-100 text-slate-600 ring-slate-200',
  trainer: 'bg-amber-50 text-amber-600 ring-amber-200',
  officer: 'bg-slate-100 text-slate-600 ring-slate-200',
  trained: 'bg-violet-50 text-violet-600 ring-violet-200',
  consultant: 'bg-emerald-50 text-emerald-600 ring-emerald-200',
};

const STATUS_BADGE = {
  active: 'bg-emerald-50 text-emerald-600 ring-emerald-200',
  onboarding: 'bg-sky-50 text-sky-600 ring-sky-200',
  pending: 'bg-amber-50 text-amber-600 ring-amber-200',
  review: 'bg-violet-50 text-violet-600 ring-violet-200',
  suspended: 'bg-slate-100 text-slate-500 ring-slate-200',
  blocked: 'bg-red-50 text-red-600 ring-red-200',
};

const COUNTRY_CODES = [
  { code: '+61', label: 'AU', flag: '🇦🇺' },
  { code: '+64', label: 'NZ', flag: '🇳🇿' },
  { code: '+44', label: 'UK', flag: '🇬🇧' },
  { code: '+1', label: 'US', flag: '🇺🇸' },
  { code: '+91', label: 'IN', flag: '🇮🇳' },
  { code: '+65', label: 'SG', flag: '🇸🇬' },
];

const APP_TYPES = [
  'Agency Compliance Officer',
  'Corporate Owner',
  'Trainer / Manager',
  'Agency Officer',
  'Trained / Manager',
  'Independent Consultant',
];

/* Status filter tabs */
const TABS = ['All', 'Active', 'Onboarding', 'Pending', 'In review', 'Blocked'];

/* ------------------------------------------------------------------ */

/* ---------- Add Customer modal ---------- */
function AddCustomerModal({ open, onClose, onSave }) {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    appType: APP_TYPES[0],
    cc: '+61',
    mobile: '',
  });
  const [errors, setErrors] = useState({});
  const [appOpen, setAppOpen] = useState(false);
  const [appQuery, setAppQuery] = useState('');

  const appOptions = APP_TYPES.filter((t) =>
    t.toLowerCase().includes(appQuery.trim().toLowerCase()),
  );

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  if (!open) return null;

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = 'Required';
    if (!form.lastName.trim()) e.lastName = 'Required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = 'Enter a valid email';
    if (!form.mobile.replace(/\D/g, '').trim()) e.mobile = 'Required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = () => {
    if (!validate()) return;
    onSave(form);
    setForm({
      firstName: '',
      lastName: '',
      email: '',
      appType: APP_TYPES[0],
      cc: '+61',
      mobile: '',
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div
        className="absolute inset-0 bg-ink/45 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
      />

      <div className="relative w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-2xl shadow-lift animate-modal-in overflow-hidden">
        {/* Clean header — soft white, no heavy gradient */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100 bg-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand/15 to-brand/5 ring-1 ring-brand/20 flex items-center justify-center text-brand">
              <UserPlus size={18} strokeWidth={1.9} />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-900">
                Invite customer
              </h2>
              <p className="text-[11px] text-slate-400">
                Send an invite so they can complete onboarding
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 max-h-[70vh] overflow-y-auto">
          <div className="space-y-6">
            {/* Personal details */}
            <section>
              <SectionLabel icon={User} title="Personal details" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                <Field
                  label="First name"
                  icon={User}
                  required
                  error={errors.firstName}
                >
                  <input
                    value={form.firstName}
                    onChange={(e) => set('firstName', e.target.value)}
                    placeholder="Jane"
                    className={inputCls(errors.firstName)}
                  />
                </Field>
                <Field
                  label="Last name"
                  icon={User}
                  required
                  error={errors.lastName}
                >
                  <input
                    value={form.lastName}
                    onChange={(e) => set('lastName', e.target.value)}
                    placeholder="Doe"
                    className={inputCls(errors.lastName)}
                  />
                </Field>
              </div>
            </section>

            {/* Contact */}
            <section>
              <SectionLabel icon={Mail} title="Contact" />
              <div className="space-y-3.5">
                <Field label="Email" icon={Mail} required error={errors.email}>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => set('email', e.target.value)}
                    placeholder="jane@company.com.au"
                    className={inputCls(errors.email)}
                  />
                </Field>
                <Field
                  label="Mobile"
                  icon={Phone}
                  required
                  error={errors.mobile}
                >
                  <div className="flex gap-2">
                    <div className="relative shrink-0">
                      <select
                        value={form.cc}
                        onChange={(e) => set('cc', e.target.value)}
                        className="h-11 rounded-xl border border-slate-200 bg-slate-50/60 pl-2.5 pr-7 text-sm font-semibold text-slate-700 shadow-sm focus:outline-none focus:border-brand focus:ring-4 focus:ring-brand/10 cursor-pointer appearance-none transition-all duration-200"
                      >
                        {COUNTRY_CODES.map((c) => (
                          <option key={c.code} value={c.code}>
                            {c.label} {c.code}
                          </option>
                        ))}
                      </select>
                      <ChevronDown
                        size={14}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                      />
                    </div>
                    <input
                      value={form.mobile}
                      onChange={(e) =>
                        set('mobile', e.target.value.replace(/[^\d\s]/g, ''))
                      }
                      placeholder="412 345 678"
                      className={inputCls(errors.mobile, 'flex-1')}
                    />
                  </div>
                </Field>
              </div>
            </section>

            {/* Role */}
            <section>
              <SectionLabel icon={BadgeCheck} title="Application type" />
              <div className="relative" data-searchselect>
                <button
                  type="button"
                  onClick={() => setAppOpen((o) => !o)}
                  onBlur={() => setTimeout(() => setAppOpen(false), 150)}
                  className="w-full h-11 rounded-xl border border-slate-200 bg-white pl-3.5 pr-10 text-sm text-left text-slate-800 shadow-sm transition-all duration-200 hover:border-slate-300 focus:outline-none focus:border-brand focus:ring-4 focus:ring-brand/10 flex items-center justify-between"
                >
                  <span className="truncate">{form.appType}</span>
                  <ChevronDown
                    size={16}
                    className={`text-slate-400 transition-transform duration-200 ${appOpen ? 'rotate-180' : ''}`}
                  />
                </button>

                {appOpen && (
                  <div className="absolute z-20 mt-2 w-full rounded-xl border border-slate-200 bg-white shadow-lift overflow-hidden">
                    <div className="p-2 border-b border-slate-100">
                      <div className="relative">
                        <Search
                          size={14}
                          className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                        />
                        <input
                          autoFocus
                          value={appQuery}
                          onChange={(e) => setAppQuery(e.target.value)}
                          placeholder="Search application type…"
                          className="w-full h-9 pl-9 pr-3 rounded-lg border border-slate-200 bg-slate-50/60 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/10 focus:bg-white transition-all"
                        />
                      </div>
                    </div>
                    <div className="max-h-48 overflow-y-auto py-1">
                      {appOptions.map((t) => {
                        const active = form.appType === t;
                        return (
                          <button
                            type="button"
                            key={t}
                            onClick={() => {
                              set('appType', t);
                              setAppOpen(false);
                              setAppQuery('');
                            }}
                            className={`w-full flex items-center gap-2 px-3.5 py-2.5 text-left text-[12.5px] font-medium transition-colors ${
                              active
                                ? 'bg-brand/5 text-brand'
                                : 'text-slate-700 hover:bg-slate-50'
                            }`}
                          >
                            <span
                              className={`w-4 h-4 rounded-md border flex items-center justify-center transition-colors ${
                                active
                                  ? 'border-brand bg-brand text-white'
                                  : 'border-slate-300'
                              }`}
                            >
                              {active && <Check size={11} strokeWidth={3} />}
                            </span>
                            {t}
                          </button>
                        );
                      })}
                      {appOptions.length === 0 && (
                        <p className="px-3.5 py-3 text-[12px] text-slate-400">
                          No application type matches “{appQuery}”
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </section>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2.5 px-6 py-4 border-t border-slate-100 bg-slate-50/50">
          <button
            onClick={onClose}
            className="h-11 px-4 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-600 hover:bg-slate-100 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-gradient-to-r from-brand to-brand-dark text-white text-sm font-bold shadow-lg shadow-brand/25 hover:shadow-brand/40 hover:-translate-y-0.5 transition-all active:scale-[0.97] hover:-translate-y-0.5 hover:shadow-lg"
          >
            <Plus size={15} />
            Send invite
          </button>
        </div>
      </div>
    </div>
  );
}
/* Shared input styling so every field matches */
const inputCls = (error, extra = '') =>
  `w-full h-11 rounded-xl border bg-white pl-3.5 pr-3.5 text-sm text-slate-800 placeholder:text-slate-400 shadow-sm transition-all duration-200 focus:outline-none focus:border-brand focus:ring-4 focus:ring-brand/10 ${
    error
      ? 'border-red-300 bg-red-50/40 focus:ring-red-100'
      : 'border-slate-200 hover:border-slate-300 focus:bg-white'
  } ${extra}`;

/* Small section header inside the modal form */
function SectionLabel({ icon: Icon, title }) {
  return (
    <div className="flex items-center gap-2 mb-2.5">
      <span className="w-5 h-5 rounded-md bg-brand/10 flex items-center justify-center text-brand">
        <Icon size={11} strokeWidth={2.2} />
      </span>
      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
        {title}
      </span>
      <span className="h-px flex-1 bg-slate-100" />
    </div>
  );
}

function Field({ label, required, error, children }) {
  return (
    <label className="block">
      <span className="flex items-center gap-1 text-[11px] font-semibold text-slate-500 mb-1.5">
        {label}
        {required && <span className="text-brand">*</span>}
        {error && (
          <span className="text-red-500 font-medium ml-1">{error}</span>
        )}
      </span>
      {children}
    </label>
  );
}

/* ================================================================== */
/*  Main People page                                                   */
/* ================================================================== */
const PAGE_SIZE = 6;

export default function PeoplePage() {
  const [tab, setTab] = useState('All');
  const [query, setQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [people, setPeople] = useState(PEOPLE);
  const [toast, setToast] = useState(null);
  const [page, setPage] = useState(1);

  /* Map a filter label to its statusKind */
  const statusFor = (label) =>
    label === 'Active'
      ? 'active'
      : label === 'Onboarding'
        ? 'onboarding'
        : label === 'Pending'
          ? 'pending'
          : label === 'In review'
            ? 'review'
            : label === 'Blocked'
              ? 'blocked'
              : null;

  const counts = {
    All: people.length,
    Active: people.filter((p) => p.statusKind === 'active').length,
    Onboarding: people.filter((p) => p.statusKind === 'onboarding').length,
    Pending: people.filter((p) => p.statusKind === 'pending').length,
    'In review': people.filter((p) => p.statusKind === 'review').length,
    Blocked: people.filter((p) => p.statusKind === 'blocked').length,
  };

  const filtered = people.filter((p) => {
    const kind = statusFor(tab);
    const inTab = tab === 'All' || (kind && p.statusKind === kind);
    const q = query.trim().toLowerCase();
    const inQuery =
      !q ||
      p.name.toLowerCase().includes(q) ||
      p.email.toLowerCase().includes(q) ||
      p.type.toLowerCase().includes(q);
    return inTab && inQuery;
  });

  /* Paginated slice */
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const paginated = filtered.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE,
  );
  const rangeStart = filtered.length ? (safePage - 1) * PAGE_SIZE + 1 : 0;
  const rangeEnd = Math.min(safePage * PAGE_SIZE, filtered.length);

  const gotoPage = (p) => setPage(Math.max(1, Math.min(p, pageCount)));
  const resetPage = (fn) => (arg) => {
    setPage(1);
    fn(arg);
  };

  const addCustomer = (form) => {
    const fullName = `${form.firstName} ${form.lastName}`.trim();
    const type = form.appType;
    const typeKind = type.toLowerCase().includes('owner')
      ? 'owner'
      : type.toLowerCase().includes('compliance')
        ? 'compliance'
        : type.toLowerCase().includes('trainer')
          ? 'trainer'
          : type.toLowerCase().includes('manager')
            ? 'trained'
            : type.toLowerCase().includes('consultant')
              ? 'consultant'
              : 'officer';
    const initials = (form.firstName[0] || '') + (form.lastName[0] || '');
    setPeople((p) => [
      {
        id: Date.now(),
        name: fullName,
        email: form.email,
        type,
        typeKind,
        status: 'Active',
        statusKind: 'active',
        start: `Company · ${new Date().toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })}`,
        last: 'Just now',
        initial: initials,
        mobile: `${form.cc} ${form.mobile}`,
      },
      ...p,
    ]);
    setToast(`Customer “${fullName}” added`);
    setTimeout(() => setToast(null), 2600);
  };

  /* Toggle a person between blocked and active */
  const toggleBlock = (id) => {
    setPeople((prev) =>
      prev.map((p) =>
        p.id === id
          ? p.statusKind === 'blocked'
            ? { ...p, statusKind: 'active', status: 'Active' }
            : { ...p, statusKind: 'blocked', status: 'Blocked' }
          : p,
      ),
    );
  };

  return (
    <div className="min-h-screen bg-white text-slate-900 font-sans">
      <main className="px-4 md:px-6 lg:px-8 pt-6 md:pt-8 pb-14 max-w-[1300px] mx-auto">
        {/* ---------- Hero header band ---------- */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#1B2668] via-[#375AE7] to-[#5B7CFA] shadow-lift">
          <div className="absolute -top-24 right-10 w-80 h-80 rounded-full bg-white/10 blur-3xl animate-drift pointer-events-none" />
          <div className="absolute -bottom-28 -left-16 w-80 h-80 rounded-full bg-[#22D3EE]/20 blur-3xl animate-drift-slow pointer-events-none" />

          <div className="relative p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6">
            <div className="flex-1 min-w-0">
              <h1 className="mt-1.5 text-2xl md:text-3xl font-bold tracking-tight text-white flex items-center gap-2.5">
                People
                <span className="w-9 h-9 rounded-xl bg-white/15 ring-1 ring-white/25 flex items-center justify-center">
                  <Users size={18} strokeWidth={1.9} className="text-white" />
                </span>
              </h1>
              <p className="mt-1.5 text-sm text-white/75">
                Manage your people, their access, and their status in one place.
                Add new customers, view their details, and keep track of their
                activity.
              </p>
            </div>

            <div className="flex items-center gap-2.5 shrink-0">
              <button
                onClick={() => setModalOpen(true)}
                className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-white text-brand text-sm font-bold shadow-md hover:bg-white/90 transition-all active:scale-[0.97]"
              >
                <Plus size={16} />
                Invite customer
              </button>
            </div>
          </div>
        </div>

        {/* ---------- Stat chips ---------- */}
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {[
            {
              label: 'Total',
              value: people.length,
              icon: Users,
              color: '#375AE7',
            },
            {
              label: 'Active',
              value: counts.Active,
              icon: BadgeCheck,
              color: '#10B981',
            },
            {
              label: 'Onboarding',
              value: counts.Onboarding,
              icon: UserCog,
              color: '#0EA5E9',
            },
            {
              label: 'Pending',
              value: counts.Pending,
              icon: Clock,
              color: '#F59E0B',
            },
            {
              label: 'In review',
              value: counts['In review'],
              icon: Eye,
              color: '#8B5CF6',
            },
            {
              label: 'Blocked',
              value: counts.Blocked,
              icon: ShieldAlert,
              color: '#EF4444',
            },
          ].map((s, i) => {
            const Icon = s.icon;
            return (
              <div
                key={s.label}
                className="animate-fade-up flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3.5 shadow-sm hover:shadow-card hover:-translate-y-0.5 transition-all duration-200"
                style={{ animationDelay: `${i * 60}ms` }}
              >
                <span
                  className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: `${s.color}1A`, color: s.color }}
                >
                  <Icon size={17} strokeWidth={1.9} />
                </span>
                <div>
                  <p className="text-xl font-bold text-slate-900 tabular-nums leading-6">
                    {s.value}
                  </p>
                  <p className="text-[11px] text-slate-500">{s.label}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* ---------- Filter row ---------- */}
        <div className="mt-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          {/* Pill tabs */}
          <div className="inline-flex items-center gap-1 rounded-full bg-white p-1 ring-1 ring-slate-200 shadow-sm w-fit">
            {TABS.map((t) => {
              const active = tab === t;
              return (
                <button
                  key={t}
                  onClick={() => {
                    setPage(1);
                    setTab(t);
                  }}
                  className={`relative inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-full transition-all duration-300 ${
                    active
                      ? 'bg-gradient-to-r from-[#375AE7] to-[#5B7CFA] text-white shadow-md shadow-brand/25'
                      : 'text-slate-500 hover:text-slate-800'
                  }`}
                >
                  {t}
                  <span
                    className={`text-[10px] font-bold rounded-full px-1.5 tabular-nums ${
                      active
                        ? 'bg-white/25 text-white'
                        : 'bg-slate-100 text-slate-400'
                    }`}
                  >
                    {counts[t] ?? 0}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Search */}
          <div className="relative">
            <Search
              size={15}
              className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              value={query}
              onChange={(e) => {
                setPage(1);
                setQuery(e.target.value);
              }}
              placeholder="Search people, email, type…"
              className="h-11 pl-10 pr-4 w-full sm:w-64 rounded-full border border-slate-200 bg-white text-sm text-slate-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand transition"
            />
          </div>
        </div>

        {/* ---------- People table (card rows) ---------- */}
        <div className="mt-4 bg-white rounded-2xl border border-slate-200/70 shadow-card overflow-hidden">
          {/* Column header */}
          <div className="hidden md:grid grid-cols-12 gap-3 px-5 py-3 bg-gradient-to-r from-[#FAFBFF] to-[#F1F5FC] border-b border-slate-100 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
            <span className="col-span-1 text-center">Avatar</span>
            <span className="col-span-3">Person</span>
            <span className="col-span-3">Type</span>
            <span className="col-span-2">Status</span>
            <span className="col-span-3">Start · Last active</span>
          </div>

          {/* Rows */}
          <div className="divide-y divide-slate-100">
            {paginated.map((p, idx) => (
              <div
                key={p.id}
                className="group relative grid grid-cols-1 md:grid-cols-12 items-center gap-3 md:gap-3 px-5 py-4 transition-all duration-200 hover:bg-[#F8FAFF] cursor-pointer"
                style={{
                  animation: `fade-up 480ms ${idx * 45}ms cubic-bezier(.22,1,.36,1) both`,
                }}
              >
                {/* hover accent rail */}
                <span className="absolute left-0 top-0 bottom-0 w-1 rounded-r-full bg-gradient-to-b from-[#375AE7] to-[#5B7CFA] scale-y-0 group-hover:scale-y-100 origin-center transition-transform duration-300" />

                {/* Avatar */}
                <div className="md:col-span-1 md:text-center">
                  <span
                    className="relative inline-flex w-10 h-10 rounded-full items-center justify-center text-[11px] font-bold transition-transform duration-200 group-hover:scale-110"
                    style={{
                      background: (AVATARS[p.typeKind] ?? AVATARS.officer).bg,
                      color: (AVATARS[p.typeKind] ?? AVATARS.officer).text,
                    }}
                  >
                    {p.initial}
                  </span>
                </div>

                {/* Person */}
                <div className="md:col-span-3">
                  <p className="text-sm font-semibold text-slate-800 group-hover:text-brand transition-colors">
                    {p.name}
                  </p>
                  <p className="text-[11px] text-slate-400 truncate">
                    {p.email}
                  </p>
                </div>

                {/* Type */}
                <div className="md:col-span-3">
                  <span
                    className={`inline-flex items-center gap-1 text-[10px] font-semibold rounded-md px-2 py-1 ring-1 ${TYPE_BADGE[p.typeKind] ?? TYPE_BADGE.officer}`}
                  >
                    {p.type}
                  </span>
                </div>

                {/* Status */}
                <div className="md:col-span-2">
                  <span
                    className={`inline-flex items-center gap-1.5 text-[10px] font-bold rounded-full px-2 py-0.5 ring-1 ${STATUS_BADGE[p.statusKind] ?? STATUS_BADGE.active}`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse-dot" />
                    {p.status}
                  </span>
                  {p.awaiting && (
                    <p className="text-[10px] text-slate-400 mt-1">
                      {p.awaiting}
                    </p>
                  )}
                </div>

                {/* Start + last active + action */}
                <div className="md:col-span-3 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-[11px] text-slate-500 whitespace-nowrap">
                      {p.start}
                    </p>
                    <p className="text-[11px] text-slate-400 whitespace-nowrap">
                      {p.last}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleBlock(p.id);
                      }}
                      className={`inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg text-[11px] font-semibold border transition-all duration-200 active:scale-95 ${
                        p.statusKind === 'blocked'
                          ? 'text-emerald-700 bg-emerald-50 border-emerald-200 hover:bg-emerald-100'
                          : 'text-red-600 bg-white border-red-200 hover:bg-red-50'
                      }`}
                    >
                      {p.statusKind === 'blocked' ? (
                        <>
                          <Unlock size={13} /> Unblock
                        </>
                      ) : (
                        <>
                          <Lock size={13} /> Block
                        </>
                      )}
                    </button>
                    <button className="hidden md:inline-flex w-8 h-8 rounded-lg items-center justify-center text-slate-400 bg-slate-50 ring-1 ring-slate-100 opacity-0 group-hover:opacity-100 group-hover:text-brand transition-all duration-200 hover:bg-brand hover:text-white">
                      <ArrowUpRight size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {filtered.length === 0 && (
              <div className="py-16 text-center">
                <Users size={30} className="mx-auto text-slate-300" />
                <p className="text-sm text-slate-500 mt-3">
                  No people match your filters
                </p>
              </div>
            )}
          </div>
        </div>

        {/* ---------- Pagination ---------- */}
        <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-[11px] text-slate-400">
            Showing{' '}
            <b className="text-slate-600">
              {rangeStart}–{rangeEnd}
            </b>{' '}
            of {filtered.length} people
            {filtered.length !== people.length ? ` (of ${people.length})` : ''}
          </p>

          {pageCount > 1 && (
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => gotoPage(safePage - 1)}
                disabled={safePage === 1}
                className="h-9 px-3 rounded-lg text-xs font-semibold text-slate-600 border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Prev
              </button>
              {Array.from({ length: pageCount }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  onClick={() => gotoPage(p)}
                  className={`h-9 w-9 rounded-lg text-xs font-bold transition-all duration-200 ${
                    safePage === p
                      ? 'bg-gradient-to-r from-[#375AE7] to-[#5B7CFA] text-white shadow-md shadow-brand/25'
                      : 'text-slate-600 border border-slate-200 bg-white hover:bg-slate-50'
                  }`}
                >
                  {p}
                </button>
              ))}
              <button
                onClick={() => gotoPage(safePage + 1)}
                disabled={safePage === pageCount}
                className="h-9 px-3 rounded-lg text-xs font-semibold text-slate-600 border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </main>

      {/* ---------- Add customer modal ---------- */}
      <AddCustomerModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={addCustomer}
      />

      {/* ---------- Toast ---------- */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 bg-slate-900 text-white text-sm font-semibold rounded-xl px-4 py-3 shadow-lift animate-toast-in">
          <Check size={16} className="text-emerald-400" />
          {toast}
        </div>
      )}
    </div>
  );
}
